/*
 * Copyright (C) 2024 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { DialogService } from '@app/app/shared/helpers/dialog/dialog.service';
import { FeedbackStatsType, Organization, ReportFeedbackStatsSection, UpdateReportFeedbackStatsRequest } from '@app/app/shared/models';
import { UserAuthState } from '@app/app/user-auth/store/user-auth.state';
import { TranslocoService } from '@ngneat/transloco';
import { Select } from '@ngxs/store';
import { Observable } from 'rxjs';

@Component({
  selector: 'ptl-feedback-stats-report-section',
  templateUrl: './feedback-stats-section.component.html',
  styleUrls: [
    './feedback-stats-section.component.scss',
    '../../../view/diagnostics-view-report/feedback/stats-section/stats-section.component.scss',
  ],
})
export class FeedbackStatsSectionComponent implements OnChanges {
  @Input() section: ReportFeedbackStatsSection;

  @Output() moveUp = new EventEmitter<void>();
  @Output() sectionDeleted = new EventEmitter<void>();

  @Output() sectionUpdate = new EventEmitter<UpdateReportFeedbackStatsRequest>();

  @Select(UserAuthState.organizationDetails)
  organizationData$: Observable<Organization>;

  averageFeedbackScoreEnable = true;
  averageRatingEnable = true;
  distanceTravelEnable = true;
  consensusEnable = true;
  workspaceBenchmarkEnable = true;
  notionalBenchmarkEnable = true;

  isEditingTitle = false;

  constructor(
    private dialogService: DialogService,
    private translocoService: TranslocoService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    this.setStatsVisibility();
  }

  openDialog(sectionUid: string): void {
    if (!sectionUid) {
      this.sectionDeleted.next();
      return;
    }
    this.dialogService
      .showConfirmDialog(this.translocoService.translate('translations.dialog.title.removeSection'), this.translocoService)
      .then((confirmed) => {
        if (confirmed) {
          this.sectionDeleted.next();
        }
      });
  }

  updateStatsVisibility(value: boolean, type: FeedbackStatsType): void {
    const data: UpdateReportFeedbackStatsRequest = {
      stats: [
        { type: 'AVG_SCORE', enabled: type === 'AVG_SCORE' ? value : this.averageFeedbackScoreEnable },
        { type: 'AVG_RATING', enabled: type === 'AVG_RATING' ? value : this.averageRatingEnable },
        { type: 'DISTANCE_TRAVELLED', enabled: type === 'DISTANCE_TRAVELLED' ? value : this.distanceTravelEnable },
        { type: 'CONSENSUS', enabled: type === 'CONSENSUS' ? value : this.consensusEnable },
        { type: 'NATIONAL_BENCHMARK', enabled: type === 'NATIONAL_BENCHMARK' ? value : this.notionalBenchmarkEnable },
        { type: 'WORKSPACE_BENCHMARK', enabled: type === 'WORKSPACE_BENCHMARK' ? value : this.workspaceBenchmarkEnable },
      ],
      title: this.section?.title?.trim() || undefined,
    };
    this.sectionUpdate.emit(data);
  }

  startEditTitle(): void {
    this.isEditingTitle = true;

    setTimeout(() => {
      const input = document.querySelector('.f_feedback-stats-title-input') as HTMLInputElement;
      if (input) {
        input.focus();
      }
    });
  }

  saveTitle(title: string): void {
    this.isEditingTitle = false;

    const data: UpdateReportFeedbackStatsRequest = {
      stats: [
        { type: 'AVG_SCORE', enabled: this.averageFeedbackScoreEnable },
        { type: 'AVG_RATING', enabled: this.averageRatingEnable },
        { type: 'DISTANCE_TRAVELLED', enabled: this.distanceTravelEnable },
        { type: 'CONSENSUS', enabled: this.consensusEnable },
        { type: 'NATIONAL_BENCHMARK', enabled: this.notionalBenchmarkEnable },
        { type: 'WORKSPACE_BENCHMARK', enabled: this.workspaceBenchmarkEnable },
      ],
      title: title,
    };

    this.sectionUpdate.emit(data);
  }

  private setStatsVisibility(): void {
    if (this.section && this.section.feedbackStats) {
      for (const stat of this.section.feedbackStats) {
        switch (stat.type) {
          case 'AVG_SCORE':
            this.averageFeedbackScoreEnable = stat.enabled;
            break;
          case 'AVG_RATING':
            this.averageRatingEnable = stat.enabled;
            break;
          case 'DISTANCE_TRAVELLED':
            this.distanceTravelEnable = stat.enabled;
            break;
          case 'CONSENSUS':
            this.consensusEnable = stat.enabled;
            break;
          case 'NATIONAL_BENCHMARK':
            this.notionalBenchmarkEnable = stat.enabled;
            break;
          case 'WORKSPACE_BENCHMARK':
            this.workspaceBenchmarkEnable = stat.enabled;
            break;
        }
      }
    }
  }
}
